<template>
  <modal
    v-if="show"
    width="1200px"
    height="950px"
    bodyPadding="0"
    :title="modalTitle"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${$i18n.locale}/create-custom-traffic-source`"
  >
    <template v-if="step === 1">
      <div class="dialog-wrapper">
        <div class="details details-left">
          <div class="subtitle">
            1. {{t('Traffic source details')}}
          </div>

          <div class="p-16 campaign-details__wrap">
            <div>
              <workspace-select
                :form="form"
                @change-workspace="onChangeWorkspace"
                :labelHelpText="t('Workspace allows to make the required traffic source or other element accessible only by a specific member or group of members')"
                :disabled="mode === 'edit'"
                :workspacesList="workspacesList"
                attach
              ></workspace-select>
            </div>
            <div v-bind="getIntroParams(2)">
              <ui-input
                v-model="form.name"
                class="step-z"
                :placeholder="t('Name')"
                :label="t('Name') + '*'"
                name="name"
                :hide-details="2"
                :error-messages="errorsDict['name']"
              ></ui-input>
            </div>

            <div :class="{'plan-limits-warning__tags': showPlanLimitsWarning_tags}">
              <ui-select
                planLimits="tagsTrafficSources"
                v-model="form.tags"
                :placeholder="t('Select or type tags...')"
                :label="t('Tags')"
                :label-help="t('Add personalized tags to search and navigate easy through traffic sources list.')"
                :items="store.cache.tags['traffic-sources'].data"
                item-text="name"
                :hide-details="2"
                multiple
                chips
                attach
                combobox
                multi-line
                :error-messages="errorsDict['tags']"
              >
                <template #no-data>
                  <no-tag-found></no-tag-found>
                </template>
              </ui-select>
              <button v-if="showPlanLimitsWarning_tags" class="plan-limits-warning__clear" @click="clearTags">
                {{t('Reset tags')}}
              </button>
              <ui-tooltip-icon
                class="plan-limits-warning__tooltip"
                preset="error"
                v-if="showPlanLimitsWarning_tags"
                :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
              ></ui-tooltip-icon>
            </div>

            <div v-if="integrationType!=='facebook-conversions'">
              <div class="textarea__wrap">
                <ui-textarea
                  v-model="form.postbackUrl"
                  ref="textarea"
                  @input="changePostbackUrl"
                  :show-error="false"
                  :error-messages="errorsDict['postbackUrl']"
                  :hide-details="2"
                >
                  <template #label>
                    <label>
                    {{t('Postback URL')}}
                    <ui-tooltip-icon
                      preset="help"
                      class="mb-2"
                      :text="t('Use this option if you need to fire conversions back to your traffic source.')"
                    ></ui-tooltip-icon>
                    </label>
                  </template>
                </ui-textarea>
              </div>
              <div class="mb-14 mt-12 available-tokens">
                <i>{{t('Available tokens')}}:</i>
                <v-chip
                  class="tag"
                  v-for="item in chips"
                  :key="item.tag"
                  variant="flat"
                  :disabled="item.disabled"
                  @click="clickChip(item)"
                  :color="$colors.green"
                  :title="item.title"
                >{{item.tag}}</v-chip>
              </div>
            </div>
            <ui-info class="mb-8" v-if="integrationType==='facebook-conversions'">
              <span v-html="t(`With Facebook integration selected the postback URL option is not available.<br>All the conversions passing will be done via API.<br>You will need to specify the default Facebook Event Type in the <b><a href='/app/profile/integrations' target='_blank'>Integration settings</a></b>.`)" />
            </ui-info>

            <div>
              <div class="textarea__wrap">
                <label>
                  {{t('Send postbacks for selected statuses')}}
                  <ui-tooltip-icon
                    preset="help"
                    class="mb-2"
                    :text="t('This option allows to pass postbacks to your traffic source only with selected statuses.')"
                  ></ui-tooltip-icon>
                </label>
                <div class="postback_status d-flex">
                  <ui-checkbox class="pt-4 pb-4" :label="t('Approved')" v-model="form.postbackSendConfirmStatus"></ui-checkbox>
                  <ui-checkbox class="pt-4 pb-4" :label="t('____x.new')" v-model="form.postbackSendNewStatus"></ui-checkbox>
                  <ui-checkbox class="pt-4 pb-4" :label="t('Declined')" v-model="form.postbackSendDeclineStatus"></ui-checkbox>
                  <ui-checkbox class="pt-4 pb-4" :label="t('Chargeback')" v-model="form.postbackSendChargebackStatus"></ui-checkbox>
                </div>
              </div>
            </div>

            <div>
              <div class="textarea__wrap">
                <div class="d-flex align-center">
                  <ui-input
                    v-model="form.postbackPercent"
                    :label="t('Share Conversions %')"
                    :labelHelp="t('By default, BeMob sends 100% of conversions to your traffic source. Use this option to send only a selected percentage of conversions if you do not want to disclose the full conversion volume. The value set here applies to all campaigns using this traffic source. If you want to override the shaving percentage for a specific campaign, use the separate Conversions Shaving % setting available in the campaign settings.')"
                    suffix="%"
                    :max="100"
                    :has-errors="!!errorsDict['trafficLossPercent']"
                  ></ui-input>

                  <ui-tooltip-icon
                    v-if="form.postbackPercent === 0"
                    class="create-traffic-source__postback-percent-warning-tooltip"
                    preset="warning"
                    :text="t('If 0 value is specified, no conversions data will be sent to your traffic source.')"
                  ></ui-tooltip-icon>
                </div>
              </div>
            </div>

            <br>
            <hr>
            <br>

            <div class="checkbox__wrap mb-4">
              <ui-checkbox
                :label="`${t('Traffic Loss')}, %`"
                :help-tooltip="t('Setting traffic loss percent affects traffic cost in campaigns, that using cost models CPV, CPM, or Auto. When value is greater than zero total traffic cost is calculated according to the formula: (100% + traffic loss %) * cost value in campaign.')"
                v-model="useTrafficLoss"
              ></ui-checkbox>
            </div>
            <div v-if="form.useTrafficLoss">
              <div style="display: flex;">
                <ui-input
                  v-model="trafficLossPercent" 
                  suffix="%"
                  :max="100"
                  :has-errors="!!errorsDict['trafficLossPercent']"
                ></ui-input>
              </div>
            </div>
            <div :class="{'plan-limits-warning__integrations':showPlanLimitsWarning__integrations}">
              <ui-tooltip-icon
                preset="error"
                v-if="showPlanLimitsWarning__integrations"
                class="plan-limits-warning__tooltip"
                :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
              ></ui-tooltip-icon>
              <ui-select
                v-model="form.integrationId"
                :items="integrationsOptions"
                :label="t('Integration')"
                :label-help="t('Integration field is pre-populated if the traffic source is connected with BeMob via API key in Integration settings. If you wish to ignore the API Integration to set up the regular traffic source, select None.')"
                item-text="name"
                item-value="id"
                :return-object="false"
                max-width-calc
                :hide-details="2"
                menuContentClass="create-traffic-source__select-integrations-menu"
              />
            </div>
            <div class="checkbox__wrap mt-8">
              <ui-checkbox
                :label="t('Track impressions')"
                :help-tooltip="t('Mark the checkbox to track impressions for a campaigns with this traffic source.')"
                v-model="form.trackImpressions"
              ></ui-checkbox>
            </div>
          </div>
        </div>
        <div class="details details-right">
          <div class="subtitle">
            2. {{t('Parameters')}}
          </div>

          <div class="p-16">
              <div class="details-table__section">
                <div>
                  <div class="details-table__header">
                    <div class="details-table__cell details-table__cell--label"></div>
                    <div class="details-table__cell details-table__cell--field">
                      <ui-label
                        :text="t('Query param')"
                        :help-tooltip="t('Query string parameter, e.g. http://example.com/?YOUR_PARAMETER=')"
                      ></ui-label>
                    </div>
                    <div class="details-table__cell details-table__cell--field">
                      <ui-label
                        class="text-capitalize"
                        :text="t('Token')"
                        :help-tooltip="t('Token from your traffic source that will be appended to URL as parameter value, e.g. http://example.com/?parameter={\'{\'}TOKEN_EXAMPLE{\'}\'}')"
                      ></ui-label>
                    </div>
                  </div>
                </div>
                <hr>
                <div>
                  <div class="details-table__content">
                    <div class="details-table__cell details-table__cell--label">
                      {{t('External ID')}}
                      <ui-tooltip-icon
                        class="ml-4"
                        preset="help"
                        :text="labelHelpExternalID"
                      ></ui-tooltip-icon>
                    </div>
                    <div class="details-table__cell details-table__cell--field">
                      <ui-input
                        :disabled="disabled.extClickIdParam"
                        v-model="form.extClickIdParam"
                        :placeholder="t('e.g. conversions_tracking')"
                        :hide-details="1"
                        @input="changeDepends($event, 'extClickIdPlaceholder')"
                      ></ui-input>
                    </div>
                    <div class="details-table__cell details-table__cell--field">
                      <ui-input
                        :disabled="disabled.extClickIdPlaceholder"
                        v-model="form.extClickIdPlaceholder"
                        :placeholder="t('e.g. {\'{\'}conversions_tracking{\'}\'}')"
                        :hide-details="1"
                        @input="touchDepends('extClickIdPlaceholder')"
                      ></ui-input>
                    </div>
                  </div>
                  <div class="details-table__content">
                    <div class="details-table__cell details-table__cell--label">
                      {{t('Cost')}}
                      <ui-tooltip-icon
                        class="ml-4"
                        preset="help"
                        :text="t('Add this parameter if your traffic source can pass click / visitor costs. This will enable you to automatically track costs using the Auto setting in campaign setup.')"
                      ></ui-tooltip-icon>
                    </div>
                    <div class="details-table__cell details-table__cell--field">
                      <ui-input
                        v-model="form.costParam"
                        :placeholder="t('e.g. cost')"
                        :hide-details="1"
                        @input="changeDepends($event, 'costPlaceholder')"
                      ></ui-input>
                    </div>
                    <div class="details-table__cell details-table__cell--field">
                      <ui-input
                        v-model="form.costPlaceholder"
                        :placeholder="t('e.g. {\'{\'}cost{\'}\'}')"
                        :hide-details="1"
                        @input="touchDepends('costPlaceholder')"
                      ></ui-input>
                    </div>
                  </div>
                </div>
              </div>

              <div class="details-table__section">
                <div>
                  <div class="details-table__header">
                    <div class="details-table__column"></div>
                    <div class="details-table__column">
                      <ui-label
                        :text="t('Query param')"
                        :help-tooltip="t('Query string parameter, e.g. http://example.com/?YOUR_PARAMETER=')"
                      ></ui-label>
                    </div>
                    <div class="details-table__column">
                      <ui-label
                        class="text-capitalize"
                        :text="t('Token')"
                        :help-tooltip="t('Token from your traffic source that will be appended to URL as parameter value, e.g. http://example.com/?parameter={\'{\'}TOKEN_EXAMPLE{\'}\'}')"
                      ></ui-label>
                    </div>
                    <div class="details-table__column">
                      <ui-label
                        :text="t('Name')"
                        :help-tooltip="t('Custom parameter name for reporting.')"
                      ></ui-label>
                    </div>
                    <div class="details-table__column">
                      <ui-label
                        :text="t('Mask')"
                      ></ui-label>
                    </div>
                  </div>
                </div>
                <hr>
                <div>
                  <div
                    v-for="(item, index) in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
                    :key="index"
                    class="details-table__content"
                  >
                    <div class="details-table__column">
                      {{t('Custom {0}', [index+1])}}
                    </div>
                    <div class="details-table__column">
                        <ui-input
                          v-model="form['custom' + item + 'Param']"
                          :placeholder="customPlaceholders[index].param"
                          :hide-details="1"
                          @input="changeDepends($event, 'custom' + item + 'Placeholder', 'custom' + item + 'Name')"
                        ></ui-input>
                    </div>
                    <div class="details-table__column">
                      <ui-input
                        v-model="form['custom' + item + 'Placeholder']"
                        :placeholder="customPlaceholders[index].placeholder"
                        :hide-details="1"
                        @input="touchDepends('custom' + item + 'Placeholder')"
                      ></ui-input>
                    </div>
                    <div class="details-table__column">
                      <ui-input
                        v-model="form['custom' + item + 'Name']"
                        :placeholder="customPlaceholders[index].name"
                        :hide-details="1"
                        @input="touchDepends('custom' + item + 'Name')"
                      ></ui-input>
                    </div>
                    <div
                      class="details-table__column pl-4"
                      :class="{
                      'plan-limits-warning__mask':showPlanLimitsWarning_mask({key:'custom' + item + 'Masking'})
                    }">
                      <ui-checkbox
                        :planLimitsEditable="form['custom' + item + 'Masking']"
                        planLimits="trafficSourceParamMasking" v-model="form['custom' + item + 'Masking']"></ui-checkbox>
                      <ui-tooltip-icon
                        class="plan-limits-warning__tooltip"
                        preset="error"
                        :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
                        v-if="showPlanLimitsWarning_mask({key:'custom' + item + 'Masking'})"
                      ></ui-tooltip-icon>
                    </div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <section class="justify-space-between align-center xs12" style="display: flex; width: 100%" v-if="step === 1">
        <div>
          <error-aggregator
            v-model="errors"
            v-model:active="errorsControlActive"
          ></error-aggregator>
        </div>
        <div class="d-flex justify-end align-center">
          <ui-update-info v-if="mode === 'edit'" :data="form"></ui-update-info>
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            @click="checkClose"
          ></ui-button>

          <ui-button
            v-bind="getIntroParams(3)"
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            :loading="submitLoading"
            :disabled="isActionsDisabled"
            @click="submit(); tutorial.close();"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import WorkspaceSelect from '@/views/reports/pickers/wokrspace-select.vue';
import NoTagFound from '@/views/reports/views/no-tag-found.vue';
import { computed } from 'vue';
import {
  useTrafficSourceDialog,
  type TrafficSourceDialogData,
} from './useTrafficSourceDialog';

const $colors = useColors();

const props = withDefaults(defineProps<{
  data?: TrafficSourceDialogData;
}>(), {
  data: () => ({}),
});

const emit = defineEmits<{
  close: [];
}>();

const mode = computed(() => props.data.mode ?? 'create');

const {
  store,

  t,
  show,
  step,
  submitLoading,
  form,
  chips,
  textarea,
  disabled,
  labelHelpExternalID,
  errors,
  errorsControlActive,
  errorsDict,
  modalTitle,
  workspacesList,
  customPlaceholders,
  integrationType,
  integrationsOptions,
  isActionsDisabled,
  showPlanLimitsWarning_tags,
  showPlanLimitsWarning__integrations,
  trafficLossPercent,
  useTrafficLoss,
  hasPlanLimitsRestriction,
  showPlanLimitsWarning_mask,
  tutorial,
  onChangeWorkspace,
  changePostbackUrl,
  changeDepends,
  touchDepends,
  clickChip,
  checkClose,
  clearTags,
  submit,
  getIntroParams,
} = useTrafficSourceDialog(mode.value, () => props.data, () => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.checkbox{
  padding-bottom: 0;
  align-items: center;
  :deep(.ui-plan-limits){
    margin-left:0!important;
  }
}

.tag {
  :deep(.v-chip__content) {
    cursor: pointer;
  }
}

.available-tokens {
  > i {
    margin-right: 2px;
  }

  :deep(.v-chip) {
    display: inline-flex;
    height: 16px !important;
    margin: 0 2px !important;
    font-size: 11px;
    line-height: 16px;
    vertical-align: middle;

    .v-chip__content {
      height: 16px !important;
      line-height: 16px;
      font-size: 11px;
      color: var(--color-white);
      cursor: pointer;
    }
  }
}

.update-info {
  display: flex;

  :deep(.name) {
    max-width: 250px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

@media (max-width: 900px) {
  .update-info {
    display: none;
  }
}

.postback_status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: start;
  }
}

.plan-limits-warning__integrations{
  position: relative;
  &:before{
    content:'';
    display:block;
    border:1px solid var(--color-red);
    position: absolute;
    top:-2px;
    bottom:-6px;
    left:-8px;
    right:-8px;
  } 
}

.plan-limits-warning__mask{
  position: relative;
  &:before{
    content:'';
    display:block;
    border:1px solid var(--color-red);
    position: absolute;
    top: -8px;
    bottom: -8px;
    left:-1px;
    right:-20px;
  }
  .plan-limits-warning__tooltip{
    right:-16px;
    top:-2px;
  }
}

.details-table__section {
  display: flex;
  flex-direction: column;
}

.details-table__header,
.details-table__content {
  > .details-table__cell--label {
    width: 25%;
    flex-shrink: 0;
    text-align: left;
  }

  > .details-table__cell--field {
    flex: 1;
    min-width: 0;
  }
}

.details-table__header{
  display:flex;
  text-align: center;
  align-items: center;
  justify-content: space-between;
  margin: 4px 8px;
  column-gap: 8px;
}

div.details-table__column:first-child{
  width:70px;
  flex-shrink: 0;
}

div.details-table__column:last-child{
  width:37px;
  flex-shrink: 0;
  &:lang(ru) {
    min-width:46px;
    word-break: break-all;
  }
}

.details-table__column{
  width: calc((100% - 70px - 37px) / 3);
}

.details-table__content{
  display:flex;
  text-align: center;
  align-items: center;
  justify-content: space-between;
  color: var(--color-graphite);
  border-top: 1px solid var(--color-gray10);
  padding: 8px 8px;
  column-gap: 8px;
  
  div:first-child{
    text-align: left;
  }
  :deep(.v-text-field) {
    padding-top: 0 !important;
    .v-input__slot{
      margin-bottom: 0;
    }
    input {
      margin-top: 0;
    }
    .v-text-field__details{
      display: none;
    }
  }
}

@media (min-width: 960px) {
  .details-left{
    width:42%;
  }
  .details-right{
    width:58%;
  }
}

@media (max-width: 960px) {
  .details-left,
  .details-right{
    width:100%;
  }
}
</style>

<style lang="scss">
  .available-tokens .v-chip.v-chip--disabled {
    opacity: 1 !important;
    background: var(--color-gray10) !important;
    border: 1px solid var(--color-gray20) !important;

    .v-chip__content {
      color: var(--color-black);
    }
  }
  .textarea__wrap{
    >label{
      font-size: 13px;
      font-weight: bold;
    }
    .v-input.v-textarea{
      padding-top: 6px !important;
    }
  }
  .checkbox__wrap{
    .v-input__append-outer {
      margin-left: 4px;
      .v-icon{
        font-size: 14px !important;
        color: var(--color-green) !important;
      }
    }
  }
  .create-traffic-source__select-integrations-menu{
    .v-list__tile{
      &>div{
        display: flex;
        width:100%;
      }
    }

    .plan-limits-icon{
      position: relative;
      top:2px
    }
  }
  
</style>
